import {BasePackage} from 'substance'
import jxon from '@infomaker/im-jxon'
import lodash from 'lodash'
import moment from 'moment'
import {EventSourceClient, WebSocketClient} from '@infomaker/im-infocaster-web-client'
import uuidv5 from 'uuidv5'

import {UIPackage} from '../../../ui/UIPackage'
import {ContentMenuPackage} from '../contentmenu/ContentmenuPackage'
import {ContextMenuPackage} from '../contextmenu/ContextMenuPackage'
import {AnnotationMenuPackage} from '../annotationmenu/AnnotationMenuPackage'
import {ImageToolsPackage} from './image-tools/ImageToolsPackage'
import {LabelPackage} from './label/LabelPackage'

import {NilUUID} from '../../../lib/utils/NilUUID'
import {idGenerator} from '../../../lib/utils/IdGenerator'
import {debounce} from '../../../lib/utils/debounce'
import {fetchImageMeta} from '../../../lib/utils/fetchImageMeta'

import {Event} from '../../../lib/constants/Event'
import {Hook} from '../../../lib/constants/Hook'

import {OEmbedExporter} from '../../../lib/helpers/oembed/OEmbedExporter'
import {DWFileProxy} from '../../../lib/files/DWFileProxy'
import {Validator} from '../../../lib/validators/Validator'
import {DWCommand} from '../../../lib/commands/DWCommand'
import {addTraits, hasTrait, withTraits} from '../../../lib/utils/traits'
import {traitBundle} from '../../../lib/utils/traits/traitBundle'

import {DWAnnotationCommand} from '../../../lib/commands/DWAnnotationCommand'
import {SaveCommand} from '../../commands/SaveCommand'
import {EscapeCommand} from '../../commands/EscapeCommand'
import {DWFileNode} from '../../../lib/files/DWFileNode'

const DWBasePackage = {
    name: 'base',
    configure(configurator, options) {

        const {api, conceptService} = options

        const language = configurator.getWriterConfigValue('language')
        moment.locale(language)

        // Import base packages needed
        configurator.import(BasePackage)

        configurator.import(UIPackage)
        configurator.import(LabelPackage)
        configurator.import(ContentMenuPackage)
        configurator.import(ContextMenuPackage)
        configurator.import(AnnotationMenuPackage)
        configurator.import(ImageToolsPackage)

        configurator.addNode(DWFileNode)

        // Commands and keyboard shortcuts
        configurator.addCommand('SaveCommand', SaveCommand)
        configurator.addCommand('EscapeCommand', EscapeCommand)

        configurator.addKeyboardShortcut('escape', {command: 'EscapeCommand'}, true, 'Escape')
        configurator.addKeyboardShortcut({standard: {default: 'ctrl+s', mac: 'cmd+s'}}, {command: 'SaveCommand'}, true, 'save-article')

        // Expose classes and endpoint on window.writer
        configurator.expose('NilUUID', NilUUID)
        configurator.expose('event', Event) // Expose the API on the window
        configurator.expose('hook', Hook) // Expose hook constants to window
        configurator.expose('moment', moment) // Expose moment.js on window
        configurator.expose('idGenerator', idGenerator) // Expose the ID Generator helper method
        configurator.expose('lodash', lodash) // Expose the ID Generator helper method
        configurator.expose('jxon', jxon) // Expose JXON library
        configurator.expose('uuidv5', uuidv5) // Expose a UUID V5 library
        configurator.expose('debounce', debounce) // Expose a utility debounce function
        configurator.expose('fetchImageMeta', fetchImageMeta(api)) // Expose fetchImageMeta helper function loaded with api variable
        configurator.expose('infocaster', {WebSocketClient, EventSourceClient})

        configurator.expose('OEmbedExporter', OEmbedExporter) // Expose OEmbedExporter class, used to handle oembed data
        configurator.expose('ConceptService', conceptService)
        configurator.expose('NPFileProxy', DWFileProxy) // Expose NPFileProxy base class
        configurator.expose('Validator', Validator) // Expose the validator base class
        configurator.expose('WriterCommand', DWCommand) // The NPWriter base class for commands containg commandState method
        configurator.expose('NPWriterAnnotationCommand', DWAnnotationCommand) // Expose NPWriterAnnotationCommand base class

        // Expose class traits
        configurator.expose('withTraits', withTraits) // Expose ability to add traits to extended es6 classes using traits
        configurator.expose('addTraits', addTraits) // Expose ability to add traits to normal objects
        configurator.expose('hasTrait', hasTrait) // Expose helper function to check if object or class is implementing a specific trait
        configurator.expose('traitBundle', traitBundle.build(api)) // Expose collection of common Node-trait objects
    }
}

export {DWBasePackage}
